import { useEffect, useState } from 'react';
import type { JobDefinition } from '../shared/desktop-api';
import { AiqMark } from './AiqMark';
import { jobLearningAIQ } from './job-learning-aiq';
import './learning-score-button.css';
import { TooltipButton } from './TooltipButton';

/** The engine's last answer for one job: no score until it gives one, and stale once a refresh fails. */
export type AiqReading = { jobId: string; score: number | null; stale: boolean; checking: boolean };

/** What the badge shows for this job: a dash, never a made-up score, until one is measured. */
export function aiqBadgeFace(reading: AiqReading, jobId: string) {
  const current = reading.jobId === jobId;
  const score = current ? reading.score : null;
  const stale = current && reading.stale;
  const checking = current && reading.checking;
  return {
    score: score === null ? '—' : String(score),
    stale,
    label: checking ? 'AIQ…' : 'AIQ',
    tooltip: stale
      ? 'Learning & improvements · AIQ couldn’t be updated'
      : 'Learning & improvements',
    ariaLabel: `Learning & improvements, ${score === null ? 'AIQ not measured yet' : `AIQ ${score}`}${stale ? ', update unavailable' : checking ? ', checking recent learning' : ''}`,
  };
}

export function LearningScoreButton({ job, onClick }: { job: JobDefinition; onClick: () => void }) {
  const jobId = job.id;
  const [reading, setReading] = useState<AiqReading>({
    jobId: '',
    score: null,
    stale: false,
    checking: false,
  });
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const read = async () => {
      try {
        const api = window.capturePointDesktop;
        if (!api) throw new Error('Reader unavailable');
        // The badge only renders capability; layout/scalar detail belongs to
        // the full Learning report, not every five-second score refresh.
        const capability = await api.getLearningCapability(jobId);
        const measured = jobLearningAIQ(job, [], { capability });
        if (!measured) throw new Error('Learning score unavailable');
        if (!stopped)
          setReading({
            jobId,
            score: measured.score,
            stale: false,
            checking: capability.evaluationState === 'checking',
          });
      } catch {
        if (!stopped)
          setReading((previous) => ({
            jobId,
            score: previous.jobId === jobId ? previous.score : null,
            stale: true,
            checking: false,
          }));
      } finally {
        if (!stopped) timer = setTimeout(() => void read(), 5000);
      }
    };
    void read();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [jobId]);
  const face = aiqBadgeFace(reading, job.id);
  return (
    <TooltipButton
      type="button"
      className={`learning-score-button${face.stale ? ' is-stale' : ''}`}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      aria-label={face.ariaLabel}
      tooltip={face.tooltip}
    >
      {/* The score sits on the brain it measures; AIQ, its name, sits underneath. */}
      <span className="learning-score-mark">
        <AiqMark />
        <strong key={`${job.id}:${face.score}`}>{face.score}</strong>
      </span>
      <small className="learning-score-label">{face.label}</small>
    </TooltipButton>
  );
}
